// pages/Rack.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table,
    Button,
    Space,
    Typography,
    message,
    Tag,
    Tooltip,
    Row,
    Col,
    Card,
    Input,
    DatePicker,
    Modal,
    Descriptions,
    Badge,
    Dropdown,
    Checkbox,
    Statistic
} from 'antd';
import {
    ReloadOutlined,QuestionCircleOutlined,
    SearchOutlined,
    PrinterOutlined,
    FileTextOutlined,
    UserOutlined,
    EyeOutlined,
    SwapOutlined,
    DollarOutlined,
    FileExcelOutlined,
    LeftOutlined,
    RightOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Search } = Input;

const Rack = () => {
    // --- Estados ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [searchHab, setSearchHab] = useState('');
    const [filterAgencia, setFilterAgencia] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const [ocupantes, setOcupantes] = useState([]);
    const [ocupantesLoading, setOcupantesLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [showOnlyFirstOccupant, setShowOnlyFirstOccupant] = useState(false);

    const showOnlyFirstOccupantRef = useRef(showOnlyFirstOccupant);

    // --- Columnas de la tabla principal (Rack) ---
    const columns = [
        {
            title: 'Hab.',
            dataIndex: 'NumHab',
            key: 'NumHab',
            width: 80,
            fixed: 'left',
            render: (text, record) => (
                <div style={{ 
                    backgroundColor: record.esFantasma ? '#f0f0f0' : 'transparent',
                    fontWeight: record.esFantasma ? 'bold' : 'normal',
                    color: record.esFantasma ? '#ff4d4f' : 'inherit'
                }}>
                    {text}
                </div>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'Estado',
            key: 'Estado',
            width: 100,
            render: (estado, record) => {
                const config = {
                    'O': { color: 'green', text: 'OCUPADA' },
                    'R': { color: 'orange', text: 'RESERVADA' },
                    'B': { color: 'red', text: 'BLOQUEO' },
                    'S': { color: 'purple', text: 'SALIDA' },
                    'L': { color: 'blue', text: 'LIBRE' },
                };
                const { color, text } = config[estado] || { color: 'default', text: estado };
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: 'Reserva',
            dataIndex: 'NReserva',
            key: 'NReserva',
            width: 80,
            render: (text) => text > 0 ? <Tag color="cyan">{text}</Tag> : '-'
        },
        {
            title: 'Línea',
            dataIndex: 'NLinea',
            key: 'NLinea',
            width: 60
        },
        {
            title: 'Nombre',
            dataIndex: 'Nombre',
            key: 'Nombre',
            ellipsis: true,
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'T.H.',
            dataIndex: 'TipoHab',
            key: 'TipoHab',
            width: 60
        },
        {
            title: 'T.O.',
            dataIndex: 'TipoOcupa',
            key: 'TipoOcupa',
            width: 60
        },
        {
            title: 'Entrada',
            dataIndex: 'Entrada',
            key: 'Entrada',
            width: 100
        },
        {
            title: 'Salida',
            dataIndex: 'Salida',
            key: 'Salida',
            width: 100
        },
        {
            title: 'Régimen',
            dataIndex: 'Regimen',
            key: 'Regimen',
            width: 70
        },
        {
            title: 'S.E.',
            dataIndex: 'SerEntrada',
            key: 'SerEntrada',
            width: 60
        },
        {
            title: 'S.S.',
            dataIndex: 'SerSalida',
            key: 'SerSalida',
            width: 60
        },
        {
            title: 'Agencia',
            dataIndex: 'Agencia',
            key: 'Agencia',
            width: 100,
            ellipsis: true
        },
        {
            title: 'Sub-Agencia',
            dataIndex: 'SubAgencia',
            key: 'SubAgencia',
            width: 100,
            ellipsis: true
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    {record.NReserva > 0 && (
                        <>
                            <Tooltip title="Ver ocupantes">
                                <Button 
                                    type="text" 
                                    icon={<UserOutlined />} 
                                    size="small"
                                    onClick={() => handleViewOcupantes(record)}
                                />
                            </Tooltip>
                            <Tooltip title="Cambiar habitación">
                                <Button 
                                    type="text" 
                                    icon={<SwapOutlined />} 
                                    size="small"
                                    onClick={() => handleCambioHab(record)}
                                />
                            </Tooltip>
                            <Tooltip title="Cargos">
                                <Button 
                                    type="text" 
                                    icon={<DollarOutlined />} 
                                    size="small"
                                    onClick={() => handleCargos(record)}
                                />
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Ver detalles">
                        <Button 
                            type="text" 
                            icon={<EyeOutlined />} 
                            size="small"
                            onClick={() => handleViewDetalle(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    // --- Columnas de la tabla de ocupantes ---
    const ocupantesColumns = [
        {
            title: 'Ocupante',
            dataIndex: 'NumOcupante',
            key: 'NumOcupante',
            width: 80
        },
        {
            title: 'Nombre',
            dataIndex: 'Nombre',
            key: 'Nombre'
        },
        {
            title: 'T.H.',
            dataIndex: 'TipoHab',
            key: 'TipoHab',
            width: 60
        },
        {
            title: 'S.E.',
            dataIndex: 'SerEntrada',
            key: 'SerEntrada',
            width: 60
        },
        {
            title: 'S.S.',
            dataIndex: 'SerSalida',
            key: 'SerSalida',
            width: 60
        },
        {
            title: 'Régimen',
            dataIndex: 'Regimen',
            key: 'Regimen',
            width: 70
        },
        {
            title: 'Entrada',
            dataIndex: 'Entrada',
            key: 'Entrada',
            width: 100
        },
        {
            title: 'Salida',
            dataIndex: 'Salida',
            key: 'Salida',
            width: 100
        },
        {
            title: 'DNI',
            dataIndex: 'TieneDNI',
            key: 'TieneDNI',
            width: 60,
            render: (value) => value ? <Badge status="success" /> : <Badge status="default" />
        }
    ];

    // --- Funciones ---
    const fetchRack = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {
                fecha: selectedDate.format('YYYY-MM-DD')
            };
            
            // ✅ USAR LA REFERENCIA EN VEZ DEL ESTADO
            if (showOnlyFirstOccupantRef.current) {
                params.soloPrimerOcupante = 'true';
                console.log('📌 Enviando filtro: solo primer ocupante = true');
            } else {
                console.log('📌 Enviando filtro: mostrar todos los ocupantes');
            }
            
            if (filterAgencia) {
                params.agencia = filterAgencia;
            }

            console.log('📤 Parámetros enviados:', params);

            const response = await axios.get(`${BASE_URL}/api/rack`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setData(response.data.data || []);
        } catch (error) {
            console.error('❌ Error cargando Rack:', error);
            message.error('Error al cargar los datos del Rack');
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, filterAgencia]);

    const fetchOcupantes = async (numReserva, numHab) => {
        setOcupantesLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${BASE_URL}/api/rack/ocupantes/${numReserva}/${numHab}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOcupantes(response.data.data || []);
            setModalVisible(true);
        } catch (error) {
            console.error('❌ Error cargando ocupantes:', error);
            message.error('Error al cargar los ocupantes');
        } finally {
            setOcupantesLoading(false);
        }
    };

    const handleFirstOccupantChange = (e) => {
        const checked = e.target.checked;
        console.log('🔄 Checkbox cambió a:', checked);
        setShowOnlyFirstOccupant(checked);
        // 👇 Forzar la actualización inmediata de la referencia
        showOnlyFirstOccupantRef.current = checked;
        // 👇 Llamar a fetchRack después de actualizar
        fetchRack();
    };

    // --- Handlers de fecha ---
    const handlePreviousDay = () => {
        setSelectedDate(selectedDate.subtract(1, 'day'));
    };

    const handleNextDay = () => {
        setSelectedDate(selectedDate.add(1, 'day'));
    };

    const handleToday = () => {
        setSelectedDate(dayjs());
    };

    // --- Handlers de acciones ---
    const handleViewOcupantes = (record) => {
        if (record.NReserva > 0) {
            fetchOcupantes(record.NReserva, record.Habitacion);
        } else {
            message.info('No hay reserva asociada a esta habitación');
        }
    };

    const handleCambioHab = (record) => {
        message.info(`Función de cambio de habitación para ${record.NumHab}`);
        // Aquí iría la lógica para abrir el modal de cambio de habitación
    };

    const handleCargos = (record) => {
        message.info(`Función de cargos para reserva ${record.NReserva}`);
        // Aquí iría la lógica para abrir el modal de cargos
    };

    const handleViewDetalle = (record) => {
        Modal.info({
            title: `Detalles de la Habitación ${record.NumHab}`,
            width: 600,
            content: (
                <Descriptions column={2} bordered size="small" style={{ marginTop: 16 }}>
                    <Descriptions.Item label="Estado">
                        <Tag color={record.Estado === 'O' ? 'green' : record.Estado === 'R' ? 'orange' : 'blue'}>
                            {record.Texto}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Reserva">{record.NReserva || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Línea">{record.NLinea || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Nombre">{record.Nombre || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Entrada">{record.Entrada || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Salida">{record.Salida || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Régimen">{record.Regimen || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Agencia">{record.Agencia || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Sub-Agencia">{record.SubAgencia || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Observación">{record.Observacion || '-'}</Descriptions.Item>
                </Descriptions>
            )
        });
    };

    const handlePrint = () => {
        message.info('Función de impresión en desarrollo');
        // Aquí iría la lógica para imprimir el rack
    };

    const handleExportExcel = () => {
        message.info('Función de exportación a Excel en desarrollo');
    };

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
        }
    };

    // --- Efecto para cargar datos ---
    useEffect(() => {
        fetchRack();
    }, [fetchRack]);

    useEffect(() => {
        showOnlyFirstOccupantRef.current = showOnlyFirstOccupant;
    }, [showOnlyFirstOccupant]);  

    // --- Filtros ---
    // Filtro por habitación
    const filteredByHab = searchHab
        ? data.filter(item => String(item.NumHab).includes(searchHab))
        : data;

    // Filtro para mostrar solo el primer ocupante (NumOcupante = 1)
    // NOTA: El backend ya debería estar devolviendo solo NumOcupante = 1 para el rack principal
    // Este filtro es para mantener consistencia con el comportamiento de Delphi
    const filteredData = showOnlyFirstOccupant
        ? filteredByHab.filter(item => {
            // Si tiene NReserva > 0 y NLinea > 0, significa que es un ocupante
            // El backend ya filtra por NumOcupante = 1 en la consulta principal
            // Este check es adicional por si el backend devuelve otros ocupantes
            return true; // El backend ya hace el filtro
        })
        : filteredByHab;

    // --- Estadísticas ---
    const stats = {
        total: filteredData.length,
        ocupadas: filteredData.filter(d => d.Estado === 'O').length,
        reservadas: filteredData.filter(d => d.Estado === 'R').length,
        libres: filteredData.filter(d => d.Estado === 'L').length,
        bloqueos: filteredData.filter(d => d.Estado === 'B').length,
        salidas: filteredData.filter(d => d.Estado === 'S').length,
    };

    const renderTitleWithNavigation = () => (
        <Space>
            <span>Rack</span>
            <Space.Compact size="small">
                <Button 
                    icon={<LeftOutlined />} 
                    onClick={handlePreviousDay}
                    size="small"
                    title="Día anterior"
                />
                <DatePicker 
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                    size="small"
                    style={{ width: 120 }}
                    suffixIcon={<CalendarOutlined />}
                    allowClear={false}
                />
                <Button 
                    icon={<RightOutlined />} 
                    onClick={handleNextDay}
                    size="small"
                    title="Día siguiente"
                />
                <Button 
                    onClick={handleToday}
                    size="small"
                    type="primary"
                    ghost
                >
                    Hoy
                </Button>
            </Space.Compact>
        </Space>
    );    

    return (
        <PageContainer
            title={renderTitleWithNavigation()}
            subtitle={`Estado de las habitaciones para el ${selectedDate.format('DD/MM/YYYY')}`}
            loading={loading}
            onRefresh={fetchRack}
        >
            {/* Estadísticas */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={4}>
                    <Card size="small">
                        <Statistic title="Total" value={stats.total} valueStyle={{ fontSize: 24 }} />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card size="small">
                        <Statistic title="Ocupadas" value={stats.ocupadas} valueStyle={{ color: '#52c41a', fontSize: 24 }} />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card size="small">
                        <Statistic title="Reservadas" value={stats.reservadas} valueStyle={{ color: '#faad14', fontSize: 24 }} />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card size="small">
                        <Statistic title="Libres" value={stats.libres} valueStyle={{ color: '#1890ff', fontSize: 24 }} />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card size="small">
                        <Statistic title="Bloqueos" value={stats.bloqueos} valueStyle={{ color: '#ff4d4f', fontSize: 24 }} />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card size="small">
                        <Statistic title="Salidas" value={stats.salidas} valueStyle={{ color: '#722ed1', fontSize: 24 }} />
                    </Card>
                </Col>
            </Row>

            {/* Filtros y acciones */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {/* <Col span={8}>
                    <Space.Compact style={{ width: '100%' }}>
                        <Button 
                            icon={<LeftOutlined />} 
                            onClick={handlePreviousDay}
                            title="Día anterior"
                        />
                        <DatePicker 
                            value={selectedDate}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            style={{ width: '100%' }}
                            suffixIcon={<CalendarOutlined />}
                        />
                        <Button 
                            icon={<RightOutlined />} 
                            onClick={handleNextDay}
                            title="Día siguiente"
                        />
                        <Button 
                            onClick={handleToday}
                            type="primary"
                            ghost
                        >
                            Hoy
                        </Button>
                    </Space.Compact>
                </Col> */}
                <Col span={4}>
                    <Input
                        placeholder="Buscar habitación"
                        value={searchHab}
                        onChange={(e) => setSearchHab(e.target.value)}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                </Col>
                <Col span={4}>
                    <Input
                        placeholder="Filtrar por agencia"
                        value={filterAgencia}
                        onChange={(e) => setFilterAgencia(e.target.value)}
                        prefix={<SearchOutlined />}
                        allowClear
                        onPressEnter={fetchRack}
                    />
                </Col>
                <Col span={4}>
                    <Checkbox
                        checked={showOnlyFirstOccupant}
                        onChange={handleFirstOccupantChange}
                    >
                        Solo primer ocupante
                    </Checkbox>
                    <Tooltip title="Mostrar solo el primer ocupante de cada habitación (NumOcupante = 1)">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                    </Tooltip>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchRack}>
                            Refrescar
                        </Button>
                        <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                            Imprimir
                        </Button>
                        {/* <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
                            Exportar
                        </Button> */}
                    </Space>
                </Col>
            </Row>

            {/* Tabla principal */}
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey={(record) => `${record.NumHab}-${record.NReserva}-${record.NLinea}`}
                loading={loading}
                scroll={{ x: 1500, y: 500 }}
                size="middle"
                pagination={{ 
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} habitaciones`
                }}
                rowClassName={(record) => {
                    if (record.esFantasma) return 'row-fantasma';
                    return '';
                }}
            />

            {/* Modal de ocupantes */}
            <Modal
                title={
                    <Space>
                        <UserOutlined />
                        <Text strong>Ocupantes de la Habitación</Text>
                    </Space>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Cerrar
                    </Button>
                ]}
                width={900}
            >
                <Table
                    columns={ocupantesColumns}
                    dataSource={ocupantes}
                    rowKey="NumOcupante"
                    loading={ocupantesLoading}
                    size="small"
                    pagination={false}
                    scroll={{ x: 800 }}
                />
            </Modal>

            <style jsx>{`
                :global(.row-fantasma td) {
                    background-color: #fff1f0 !important;
                    color: #ff4d4f !important;
                }
                :global(.row-fantasma td .ant-tag) {
                    opacity: 0.8;
                }
            `}</style>
        </PageContainer>
    );
};

export default Rack;